import { Subcategory, QueryParams } from "@/sharedTypes";
import { baseApi } from "../baseApi";
import { flattenQueryParams } from "@/utils/requestUtils";

const subcategoryTag = "Subcategory";

export const subcategoryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSubcategoriesList: builder.query<any, Record<string, any> | void>({
      query: (params) => {
        const searchParams = new URLSearchParams();

        const { filters = {}, pagination = {} } = (params as any) || {};

        const resolvedFilters: Record<string, unknown> = { is_active: true };
        Object.entries(filters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            resolvedFilters[key] = value;
          }
        });

        Object.entries(resolvedFilters).forEach(([key, value]) => {
          if (value !== undefined && value !== null && value !== "") {
            searchParams.append(key, String(value));
          }
        });

        const resolvedPagination: Record<string, unknown> = {
          page: 1,
          pageSize: 15,
        };
        Object.entries(pagination).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            resolvedPagination[key] = value;
          }
        });

        Object.entries(resolvedPagination).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            searchParams.append(key, String(value));
          }
        });

        const queryString = searchParams.toString();
        return `/gw/subcategories${queryString ? `?${queryString}` : ""}`;
      },
      transformResponse: (response: any) => {
        const payload = response?.data ?? response;

        if (!payload) return payload;

        if (Array.isArray(payload)) {
          return {
            items: payload,
            pagination: {
              pageIndex: 1,
              pageSize: payload.length,
              totalPages: 1,
              totalItems: payload.length,
            },
          };
        }

        if (payload?.items) return payload;

        if (payload?.subcategories) {
          return {
            items: payload.subcategories,
            pagination: payload.pagination ?? payload.meta ?? {},
          };
        }

        return payload;
      },
      providesTags: [subcategoryTag],
    }),
    getSubcategoryById: builder.query<Subcategory, string>({
      query: (id) => `/gw/subcategories/${id}`,
      transformResponse: (response: any) => response?.data ?? response,
      providesTags: (_result, _error, id) => [{ type: subcategoryTag, id }],
    }),
    createSubcategory: builder.mutation<Subcategory, Partial<Subcategory>>({
      query: (payload) => ({
        url: `/gw/subcategories`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: [subcategoryTag],
    }),
    updateSubcategory: builder.mutation<Subcategory, Subcategory>({
      query: (payload) => ({
        url: `/gw/subcategories/${payload.subcategory_id}`,
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: [subcategoryTag],
    }),
    deleteSubcategory: builder.mutation<void, number>({
      query: (id) => ({
        url: `/gw/subcategories/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: [subcategoryTag],
    }),
    restoreSubcategory: builder.mutation<void, number>({
      query: (id) => ({
        url: `/gw/subcategories/${id}/restore`,
        method: "PUT",
      }),
      invalidatesTags: [subcategoryTag],
    }),
    hardDeleteSubcategory: builder.mutation<void, number>({
      query: (id) => ({
        url: `/gw/subcategories/${id}/permanent`,
        method: "DELETE",
      }),
      invalidatesTags: [subcategoryTag],
    }),
    getSubcategoryAgents: builder.query<any, { subcategoryId: string; params: QueryParams }>({
      query: ({ subcategoryId, params }) => {
        const flatParams = params ? flattenQueryParams(params) : {};
        flatParams["subcategory_id"] = subcategoryId;
        
        const queryString = new URLSearchParams(flatParams).toString();
        return `/agents/by-category?${queryString}`;
      },
      providesTags: [subcategoryTag],
    }),
  }),
});

export const {
  useGetSubcategoriesListQuery,
  useLazyGetSubcategoriesListQuery,
  useGetSubcategoryByIdQuery,
  useCreateSubcategoryMutation,
  useUpdateSubcategoryMutation,
  useDeleteSubcategoryMutation,
  useRestoreSubcategoryMutation,
  useHardDeleteSubcategoryMutation,
  useGetSubcategoryAgentsQuery,
} = subcategoryApi;
